
import React, { useEffect, useState } from "react";
import {
    DndContext,
    useDraggable,
    useDroppable
} from "@dnd-kit/core";
import { store } from "../redux/store";
import * as scheduleAction from "../redux/actions/scheduleActions";
import { Box, Button, Grid, Icon, Paper, Tooltip, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { ReactComponent as DragIcon } from '../assets/drag.svg';
import moment from "moment";
import { useToast } from "../context/ToastContext";
import { ReactComponent as LockIcon } from '../assets/lock.svg';
import types from '../redux/commons/scheduleActionStatus';
import Scrollbar from "../containers/Anatomy/Scrollbar";
import { formatUTC, getMinutes, getSlotForTime, getSlotHeight, getWeekDates, groupEventsByType, isFuture, isPast, } from "../utils/scheduleHelpers";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { trackEvent } from '../utils/firebase';
import CancelReasonDialog from "../components/CancelReasonDialog";
import { cancellationReasonLabel } from "../utils/cancellationReasons";

const HOUR_HEIGHT = 60;
const paperStyle = {
    border:'1px solid #E6E8EC',
    borderRadius: '20px',
    textAlign: "center",
    mt: 1,
    boxShadow:'none'
};
const GRID_TEMPLATE = "80px repeat(7, 1fr)";

function DraggableBox({ type, duration, setDuration }) {
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: `drag-${type}`,
        data: {
            type,
            duration
        }
    });

    const style = {
        transform: transform
            ? `translate(${transform.x}px, ${transform.y}px)`
            : undefined,
        padding: "12px",
        background: 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0px 2px 4px rgba(0,0,0,0.15)',
        color: "red",
        borderRadius: "6px",
        cursor: "grab",
        minWidth: "89px",
        height: '20px',
        marginLeft: '6px',
        borderRadius: '10px',
    };

    return (
        <div ref={setNodeRef} {...listeners} {...attributes} style={{ ...style, alignContent: 'center', display: 'flex', alignItems: 'center' }}>
            <DragIcon style={{ width: 15, height: 15, }} />
            <Typography sx={{ ml: '4px' }}>{type}</Typography>

            <div style={{ marginLeft: '5px' }}>
                <select
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    style={{ border: 'none' }}
                >
                    <option value={60}>1 hour</option>
                </select>
            </div>
        </div>
    );
}

export default function ScheduleApp() {
    const [duration, setDuration] = useState(60);
    const [surgery, setSurgery] = useState(60);
    const [pendingMove, setPendingMove] = useState(null);
    const authReducer = useSelector((state) => state.authReducer);
    const [hospitals, setHospitals] = useState(authReducer?.userAttributes?.hospitals || []);

    const [weekSlots, setWeekSlots] = useState({});
    const today = new Date();
    const [weekDates, setWeekDates] = useState(getWeekDates(today));
    const [selectedDate, setSelectedDate] = useState(() => {
        const t = new Date();
        return {
            date: t,
            iso: t.toISOString().split("T")[0],
            day: t.toLocaleDateString("en-US", { weekday: "short" }),
            label: t.getDate()
        };
    });
    const [selectedHospital, setSelectedHospital] = useState(() => {
        const userHospitals = authReducer?.userAttributes?.hospitals || [];
        if (userHospitals.length > 1) {
            return { id: 'all', name: 'All Hospitals' };
        }
        return userHospitals[0] || null;
    });
    const showToast = useToast()
    const scheduleReducer = useSelector((state) => state.scheduleReducer);
    const [blockSlot, setBlockSlot] = useState(null)
    const [unBlockSlot, setUnBlockSlot] = useState(null)
    const [deleteEvent, setDeleteEvent] = useState(null)
    const [cancelBookings, setCancelBookings] = useState(null)
    const [timeRows, setTimeRows] = useState([])
    const [manualEventType, setManualEventType] = useState("Delivery");
    const [manualDate, setManualDate] = useState(null);
    const [manualSlot, setManualSlot] = useState(null);
    const isPastWeek = weekDates.every(d => isPast(d.date));

    useEffect(() => {
        // set state from reducer to show 1 week data
        if (scheduleReducer.slotSummary) {
            const apiResponse = scheduleReducer.slotSummary;
            const formatted = {};
            apiResponse.forEach((d) => {
                formatted[d.date] = {
                    slots: d.slots,
                    events: d.events
                };
            });
            setWeekSlots(formatted);
        }
    }, [scheduleReducer])

    useEffect(() => {
        if (scheduleReducer.timeline) {
            setTimeRows(scheduleReducer.timeline);
        }
    }, [scheduleReducer])

    //  refresh slots after creating and updating event
    useEffect(() => {
        if (scheduleReducer.status === types.POST_EVENTS_SUCCESS ||
            scheduleReducer.status === types.UPDATE_EVENTS_SUCCESS) {

            if(scheduleReducer.status === types.POST_EVENTS_SUCCESS)
            {
                showToast("Event Created succesfully!", "info") 
            }
            if(scheduleReducer.status === types.UPDATE_EVENTS_SUCCESS)
            {
                showToast("Event Updated succesfully!", "info") 
            }
            const hospitalId = selectedHospital?.id === 'all' ? undefined : selectedHospital?.id;
            store.dispatch(
                scheduleAction.getSlotSummaryRequestAction(
                    weekDates?.[0]?.iso,
                    weekDates?.[weekDates.length - 1]?.iso,
                    hospitalId
                )
            );
        }
    }, [scheduleReducer.status]);

    //  refresh slots after blocking entire day
    //  refresh slots after blocking 1 slot
    // refresh slots after moving apppintment from one slot to another

    useEffect(() => {
        if (
            scheduleReducer.status === types.BLOCK_SLOT_SUMMARY__SUCCESS ||
            scheduleReducer.status === types.UPDATE_SLOT_SUMMARY__SUCCESS ||
            scheduleReducer.status === types.MOVE_BULK_APPOINTEMNTS_SUCCESS ||
            scheduleReducer.status === types.DELETE_EVENTS_SUCCESS ||
            scheduleReducer.status === types.UNBLOCK_SLOT_SUMMARY_SUCCESS ||
            scheduleReducer.status === types.CANCEL_BULK_APPOINTMENTS_SUCCESS
        ) {
            if (scheduleReducer.status === types.BLOCK_SLOT_SUMMARY__SUCCESS) {
                showToast("Day blocked succesfully!", "info")
            }
            if (scheduleReducer.status === types.DELETE_EVENTS_SUCCESS) {
                showToast("Event Deleted succesfully!", "info")
            }
            if (scheduleReducer.status === types.CANCEL_BULK_APPOINTMENTS_SUCCESS) {
                showToast("All Bookings Cancelled!", "info")
            }
            if (scheduleReducer.status === types.MOVE_BULK_APPOINTEMNTS_SUCCESS) {
                showToast("All Bookings Moved to new time!", "info")
            }

            const hospitalId = selectedHospital?.id === 'all' ? undefined : selectedHospital?.id;
            store.dispatch(
                scheduleAction.getSlotSummaryRequestAction(
                    weekDates?.[0]?.iso,
                    weekDates?.[weekDates.length - 1]?.iso,
                    hospitalId
                )
            );
        }
    }, [scheduleReducer.status]);

    useEffect(() => {
        const userHospitals = authReducer?.userAttributes?.hospitals || [];
        setHospitals(userHospitals);
        if (!selectedHospital && userHospitals.length) {
            if (userHospitals.length > 1) {
                setSelectedHospital({ id: 'all', name: 'All Hospitals' });
            } else {
                setSelectedHospital(userHospitals[0]);
            }
        }
    }, [authReducer?.userAttributes?.hospitals]);

    useEffect(() => {
        // fetch slots for weekly - when changing hospital
        if (!selectedHospital) return;
        const hospitalId = selectedHospital.id === 'all' ? undefined : selectedHospital.id;
        store.dispatch(scheduleAction.getTimelineRequestAction(hospitalId))
        store.dispatch(
            scheduleAction.getSlotSummaryRequestAction(
                weekDates?.[0]?.iso,
                weekDates?.[weekDates.length - 1]?.iso,
                hospitalId
            )
        );
    }, [selectedHospital]);

    const findSlotById = (slotId) => {
        for (const date in weekSlots) {
            const slot = weekSlots[date]?.slots?.find((s) => s.id === slotId);
            if (slot) return slot;
        }
        return null;
    };

    const handleDragEnd = (event) => {
        if (isPastWeek) {
            showToast("Cannot modify past week", "info");
            return;
        }
        const { active, over } = event;
        if (!over) return;
        const data = active.data.current;
        if (over && !findSlotById(over.id)?.isAvailable) {
            return;
        }
        // EVENT MOVE
        if (data.isEvent) {
            if (data.fromSlot === over.id) return;

            setPendingMove({
                type: "EVENT_MOVE",
                item: data.item,
                fromSlot: data.fromSlot,
                toSlot: over.id
            });
            return;
        }

        if (data.type === "BOOKING_MOVE") {
            if (data.slot.id === over.id) return;

            setPendingMove({
                type: "BOOKING_MOVE",
                fromSlot: data.slot,
                toSlot: over.id
            });
        }
        //  Move existing (non-event)
        if (data.fromSlot) {
            if (data.fromSlot === over.id) return;

            setPendingMove({
                item: data.item,
                fromSlot: data.fromSlot,
                toSlot: over.id
            });
            return;
        }

        //  New item (delivery box)
        if (data.type == "Surgery" || data.type == "Delivery") {
            setPendingMove({
                type: "NEW",
                item: data,
                toSlot: over.id
            });
        }
    };

    const confirmMove = () => {

        //  NEW DELIVERY
        if (pendingMove.type === "NEW") {
            const { item, toSlot } = pendingMove;

            const targetSlot = findSlotById(toSlot);

            if (!targetSlot) {
                setPendingMove(null);
                return;
            }

            // block validation
            if (!targetSlot.isAvailable) {
                showToast("Slot is blocked",'info')
                setPendingMove(null);
                return;
            }

            // capacity validation
            if (targetSlot.currentBookings >= targetSlot.maxBookings) {
                showToast("Slot is full",'info')
                setPendingMove(null);
                return;
            }

            const slotStart = new Date(targetSlot.startDateTime);
            const slotEnd = new Date(targetSlot.endDateTime);

            const slotDuration = (slotEnd - slotStart) / (1000 * 60);

            if (item.duration > slotDuration) {
                showToast("Event duration exceeds slot",'info')
                setPendingMove(null);
                return;
            }

            const start = new Date(slotStart);
            const end = new Date(start);
            end.setMinutes(end.getMinutes() + item.duration);

            //  CREATE EVENT API
            store.dispatch(
                scheduleAction.postScheduleEventRequestAction({
                    hospitalId: targetSlot?.hospitalId || (selectedHospital?.id !== 'all' ? selectedHospital?.id : hospitals?.[0]?.id),
                    eventType: item.type,
                    startDateTime: start.toISOString(),
                    endDateTime: end.toISOString()
                })
            );
            setPendingMove(null);
            return;
        }

        // EVENT MOVE 
        if (pendingMove.type === "EVENT_MOVE") {
            const { item, toSlot } = pendingMove;

            const targetSlot = findSlotById(toSlot);

            if (!targetSlot) {
                setPendingMove(null);
                return;
            }

            //  prevent drop on blocked slot
            if (!targetSlot.isAvailable) {
                showToast("Slot is blocked",'info')
                setPendingMove(null);
                return;
            }

            //  prevent overbooking
            if (targetSlot.currentBookings >= targetSlot.maxBookings) {
                showToast("Slot is full",'info')
                setPendingMove(null);
                return;
            }
            //   UPDATE EVENT API
            store.dispatch(
                scheduleAction.updateScheduleEventDataRequestAction(
                    {
                        hospitalId: item.hospitalId,
                        eventType: item.eventType,
                        startDateTime: targetSlot.startDateTime,
                        endDateTime: targetSlot.endDateTime
                    },
                    item.id
                )
            );

            setPendingMove(null);
            return;
        }

        if (pendingMove.type === "BOOKING_MOVE") {
            const fromSlot = pendingMove.fromSlot;
            const toSlot = findSlotById(pendingMove.toSlot);

            if (!toSlot) return;

            if (!toSlot.isAvailable) {
                showToast("Target slot blocked",'info')
                return;
            }

            const availableCapacity =
                toSlot.maxBookings - toSlot.currentBookings;

            if (fromSlot.currentBookings > availableCapacity) {
                showToast("Not enough capacity in target slot",'info')
                return;
            }

            // call bulk slot api 
            store.dispatch(scheduleAction.moveBulkAppointmentForSlotRequestAction({
                toSlotId: toSlot.id,
                fromSlotId: fromSlot.id
            }))
            setPendingMove(null);
        }
        setPendingMove(null);
    };

    function layoutEvents(events) {
        const sorted = [...events].sort(
            (a, b) => new Date(a.startDateTime) - new Date(b.startDateTime)
        );

        const result = [];

        sorted.forEach(event => {
            let placed = false;

            for (let i = 0; i < result.length; i++) {
                const column = result[i];

                const lastEvent = column[column.length - 1];

                if (new Date(event.startDateTime) >= new Date(lastEvent.endDateTime)) {
                    column.push(event);
                    placed = true;
                    break;
                }
            }

            if (!placed) {
                result.push([event]);
            }
        });

        const final = [];

        result.forEach((column, colIndex) => {
            column.forEach(event => {
                final.push({
                    ...event,
                    columnIndex: colIndex,
                    totalColumns: result.length
                });
            });
        });

        return final;
    }

    function confirmBlocking(isblock) {
        if (isblock) {
            trackEvent('schedule_slot_blocked', { method: 'single_slot' });
        } else {
            trackEvent('schedule_slot_unblocked', { method: 'single_slot' });
        }
        store.dispatch(scheduleAction.updateSlotDataRequestAction({
            isAvailable: !isblock,
        }, blockSlot ? blockSlot?.id : unBlockSlot?.id))
        setBlockSlot(null)
        setUnBlockSlot(null)
    }

    function SlotCell({ slot, children }) {
        const { setNodeRef, isOver } = useDroppable({
            id: slot?.id || "empty-slot",
            disabled:
                !slot ||
                !slot?.isAvailable ||
                slot?.currentBookings >= slot?.maxBookings
        });

        // for missing slot show empty area
        if (!slot) {
            return (
                <div
                    style={{
                        height: HOUR_HEIGHT,
                        border: "1px solid #eee"
                    }}
                />
            );
        }
        const isBlocked = !slot.isAvailable;
        const isFull = slot.currentBookings >= slot.maxBookings;
        const height = slot ? getSlotHeight(slot) : HOUR_HEIGHT;

        return (
            <div
                ref={setNodeRef}
                style={{
                    height: height,
                    padding: "4px",

                    background: isBlocked
                        ? "rgba(220, 210, 210, 0.6)"
                        : isFull
                            ? "#ffcdd2"
                            : (!isBlocked && isOver)
                                ? "#c8e6c9"
                                : "transparent",

                    border: "1px solid #ddd",
                    opacity: isBlocked || isFull ? 0.8 : 1,
                    cursor: "pointer",

                }}
                onClick={() => {
                    if (isPastWeek) {
                        showToast("Cannot modify past week", "info");
                        return;
                    }
                    if (isBlocked) {
                        setUnBlockSlot(slot)
                    }
                    !isBlocked && setBlockSlot(slot)
                }}
            >
                {children}
            </div>
        );
    }

    function EventBlockInsideSlot({ event, slot }) {
        const { attributes, listeners, setNodeRef, transform } = useDraggable({
            id: event.id,
            disabled: isPast(slot.startDateTime) || isPastWeek,
            data: {
                isEvent: true,
                item: event,
                fromSlot: slot.id
            }
        });
        const slotStart = getMinutes(slot.startDateTime);
        const start = getMinutes(event.startDateTime);
        const end = getMinutes(event.endDateTime);

        const offset = start - slotStart;
        const duration = end - start;

        const width = 100 / event.totalColumns;
        const left = event.columnIndex * width;

        return (
            <div
                ref={setNodeRef}
                style={{
                    transform: transform
                        ? `translate(${transform.x}px, ${transform.y}px)`
                        : undefined,
                    position: "absolute",
                    // top: (offset / 60) * HOUR_HEIGHT,
                    height: (duration / 60) * HOUR_HEIGHT,
                    left: `${left}%`,
                    width: `${width}%`,
                    background: "#ffcc80",
                    border: "1px solid #fff",
                    borderRadius: "6px",
                    fontSize: "11px",
                    padding: "4px",
                    boxSizing: "border-box",
                    cursor: "grab",
                    zIndex: 10,
                    flexDirection: 'row',
                    display: 'flex', justifyContent: 'center', alignItems: 'center'
                }}
                onClick={(e) => {
                    if (isPastWeek) {
                        showToast("Cannot modify past week", "info");
                        return;
                    }
                    e.stopPropagation();
                    setDeleteEvent(event)
                }}
            >
                <div {...listeners}
                    {...attributes}>
                    <DragIcon style={{ width: 15, height: 15, }} />
                </div>
                <Typography sx={{ ml: '6px' }}>{event.eventType}</Typography>
            </div>
        );
    }

    function DraggableBooking({ slot }) {
        const { attributes, listeners, setNodeRef, transform } = useDraggable({
            id: `booking-${slot.id}`,
            disabled: isPast(slot.startDateTime) || isPastWeek,
            data: {
                type: "BOOKING_MOVE",
                slot
            },
            activationConstraint: {
                distance: 15,
            },
        });

        const hospitalName = slot.hospitalName || slot.hospital?.name;

        return (
            <div
                ref={setNodeRef}
                style={{
                    transform: transform
                        ? `translate(${transform.x}px, ${transform.y}px)`
                        : undefined,
                    background: "#90caf9",
                    borderRadius: "6px",
                    cursor: "grab",
                    fontSize: "12px",
                    alignContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: HOUR_HEIGHT,
                    padding: '2px 4px',
                    boxSizing: 'border-box'
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    if (isPastWeek) {
                        showToast("Cannot modify past week", "info");
                        return;
                    }
                    setCancelBookings(slot);
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div {...listeners} {...attributes}>
                        <DragIcon style={{ width: 15, height: 15 }} />
                    </div>

                    <Typography sx={{ ml: '5px', fontSize: '12px' }}>
                        {slot.currentBookings} Bookings
                    </Typography>
                </div>
                {hospitalName && (
                    <Typography
                        sx={{
                            fontSize: '10px',
                            fontWeight: 500,
                            color: '#1565c0',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mt: '2px'
                        }}
                        title={hospitalName}
                    >
                        {hospitalName}
                    </Typography>
                )}
            </div>
        );
    }

    const blockDay = () => {
        trackEvent('schedule_slot_blocked', { method: 'full_day', date: selectedDate.iso });
        store.dispatch(scheduleAction.blockSlotForDayRequestAction({
            date: selectedDate.iso
        }))
    }

    const unblockDaySlot = (date) => {
        trackEvent('schedule_slot_unblocked', { method: 'full_day', date });
        store.dispatch(scheduleAction.unblockSlotForDayRequestAction({
            date: date
        }))
    }

    const changeWeek = (direction) => {
        trackEvent('schedule_week_changed', { direction });
        const firstDate = new Date(weekDates[0].date);
        let newStart;
        if (direction === "NEXT") {
            newStart = new Date(firstDate.setDate(firstDate.getDate() + 7));
        } else {
            newStart = new Date(firstDate.setDate(firstDate.getDate() - 7));
        }

        const newWeek = getWeekDates(newStart);
        setWeekDates(newWeek);
        setSelectedDate(newWeek[0]);
        const hospitalId = selectedHospital?.id === 'all' ? undefined : selectedHospital?.id;
        store.dispatch(
            scheduleAction.getSlotSummaryRequestAction(
                newWeek[0].iso,
                newWeek[newWeek.length - 1].iso,
                hospitalId
            )
        );
    };

    function TeamsCalendar({ weekDates, weekSlots, selectedDate }) {

        return (
            <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "80px repeat(7, 1fr)",
                        position: "sticky",
                        top: 0,
                        zIndex: 20,
                    }}
                >
                    <div />
                        <Button
                            onClick={() => changeWeek("PREV")}
                            style={{ position: "absolute", left: 0, top: 20, zIndex: 10 }}
                        >
                            <ChevronLeftIcon/>
                        </Button>

                        {/* RIGHT BUTTON */}
                        <Button
                            onClick={() => changeWeek("NEXT")}
                            style={{ position: "absolute", right: 0, top: 20, zIndex: 10 }}
                        >
                            <ChevronRightIcon/>
                        </Button>

                    {/* HEADER */}
                    {weekDates.map((d) => {
                        const isSelected = selectedDate?.iso === d.iso;
                        const future = isFuture(d.date);
                        const past = isPast(d.date);
                        const today = moment().format('YYYY-MM-DD')

                        const daySlots = weekSlots?.[d.iso]?.slots || [];
                        const allFalse = daySlots.every(item => item.isAvailable === false);
                        return (
                            <div
                                key={d.iso}
                                onClick={() => {
                                    if (past) return;
                                    setSelectedDate(d);
                                }}
                                style={{
                                    padding: "10px",
                                    textAlign: "center",
                                    cursor: past ? "not-allowed" : "pointer",
                                    color: isSelected ? "black" : past ? "#999" : "black",
                                    opacity: past ? 0.5 : 1,
                                    position: "relative",
                                    height: '50px',
                                    alignSelf: 'center',
                                    alignContent: 'center',
                                    justifySelf: 'center',
                                    borderRadius: '10px',
                                    border: (today == d.iso && !isSelected) ? '2px dashed red' : isSelected ? "1px solid #919191" : "none"
                                }}
                            >
                                <div>{d.day}</div>
                                <div>{d.label}</div>

                                {(future && daySlots.length > 0 && allFalse) && (
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            allFalse ? unblockDaySlot(d.iso) : blockDay()
                                        }}
                                        style={{
                                            position: "absolute",
                                            top: "2px",
                                            right: "4px",
                                            fontSize: "14px",
                                            color: "black", fontWeight: 'bold'
                                        }}
                                    >
                                        <Tooltip title="Click here to unlock the day" componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    fontSize: "14px",
                                                    borderRadius: "8px"
                                                }
                                            }
                                        }}><LockIcon style={{ height: '20px', width: '10px' }} /></Tooltip>
                                    </span>
                                )}


                                {(isSelected && future && daySlots.length > 0) && (
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            allFalse ? unblockDaySlot(d.iso) : blockDay()
                                        }}
                                        style={{
                                            position: "absolute",
                                            top: "2px",
                                            right: "4px",
                                            fontSize: "14px",
                                            color: "black", fontWeight: 'bold'
                                        }}
                                    >
                                        {allFalse ? <Tooltip title="Click here to unlock the day"
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        fontSize: "14px",
                                                        borderRadius: "8px"
                                                    }
                                                }
                                            }}><LockIcon style={{ height: '20px', width: '10px' }} /></Tooltip> : <Tooltip title="Click here to block the day" componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        fontSize: "14px",
                                                        borderRadius: "8px"
                                                    }
                                                }
                                            }}>✕</Tooltip>}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
                {/* TIME + GRID */}
                <Scrollbar gridTemplate="auto / 1fr 30px" padding="0 1rem" scrollerPadding="0rem" scrollerWidth="12px" style={{ height: "100%", }}  >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "80px repeat(7, 1fr)",
                            marginTop:'20px',
                            marginBottom:'120px'
                        }}
                    >
                        {timeRows.map((time, i) => (
                            <React.Fragment key={time}>
                                {/* TIME COLUMN */}
                                <div
                                    style={{
                                        height: HOUR_HEIGHT,
                                        fontSize: 12,
                                    }}
                                >
                                    {time.startTime}
                                </div>

                                {/* DAYS */}
                                {weekDates.map((d) => {
                                    const dayEvents = weekSlots?.[d.iso]?.events || [];
                                    const daySlots = weekSlots?.[d.iso]?.slots || [];
                                    const slot = getSlotForTime(daySlots, time);

                                    let laidOutEvents = [];

                                    if (slot) {
                                        const slotEvents = dayEvents.filter(ev => {
                                            const evStart = getMinutes(ev.startDateTime);
                                            const slotStart = getMinutes(slot.startDateTime);

                                            return evStart === slotStart;
                                        });

                                        laidOutEvents = layoutEvents(slotEvents);
                                    }
                                    return (
                                        <div key={d.iso} style={{ position: "relative", }}>
                                            <SlotCell slot={slot}>
                                                {/* bookings */}
                                                {slot?.currentBookings > 0 && (
                                                    <DraggableBooking slot={slot} />
                                                )}
                                                {/* events */}
                                                {slot && laidOutEvents.map(ev => (
                                                    <EventBlockInsideSlot
                                                        key={ev.id}
                                                        event={ev}
                                                        slot={slot}
                                                    />
                                                ))}
                                            </SlotCell>
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </div>
                </Scrollbar>
            </div>
        );
    }

    const handleManualCreate = () => {
        if (!manualDate || !manualSlot) {
            showToast("Please select date & slot", "info")
            return;
        }

        const slot = selectedDaySlots.find(s => s.id === manualSlot);

        if (!slot) return;

        if (!slot.isAvailable) {
            showToast("Slot is blocked","info")
            return;
        }

        if (slot.currentBookings >= slot.maxBookings) {
            showToast("Slot full","info")
            return;
        }

        store.dispatch(
            scheduleAction.postScheduleEventRequestAction({
                hospitalId: slot?.hospitalId || (selectedHospital?.id !== 'all' ? selectedHospital?.id : hospitals?.[0]?.id),
                eventType: manualEventType,
                startDateTime: slot.startDateTime,
                endDateTime: slot.endDateTime
            })
        );

        setManualSlot(null);
    };

    const groupedEvents = groupEventsByType(
        weekSlots?.[selectedDate.iso]?.events || []
    );

    const selectedDaySlots =
        (weekSlots?.[manualDate]?.slots || []).filter(
            (slot) =>
                slot.isAvailable &&
                slot.currentBookings < slot.maxBookings
        );

    return (

        <DndContext onDragEnd={handleDragEnd}>
            <Grid container spacing={2} sx={{ width: '100%', pl: { xl: 10, md: 5 }, pr: 5, }}>
                {/* LEFT */}

                <Grid size={{ md: 8, sm: 12, xs: 12 }} >
                    <Box sx={{ px: 2, }}>
                        <Paper
                            elevation={5}
                            sx={{ ...paperStyle, height: { xl: "79vh", lg: '80vh', md: '88vh' }, overflow: 'hidden' }}
                        >

                            {/* hospital list */}
                            <div style={{ display: "flex", gap: "10px", marginBottom: "20px", marginTop: '10px', paddingLeft: '20px' }}>
                                {hospitals.length > 1 && (
                                    <div
                                        onClick={() => setSelectedHospital({ id: 'all', name: 'All Hospitals' })}
                                        style={{
                                            padding: "8px 16px",
                                            borderRadius: "20px",
                                            cursor: "pointer",
                                            border:
                                                selectedHospital?.id === 'all' ? '1px solid #919191' : "none",
                                            color: "black",
                                            marginTop: '20px'
                                        }}
                                    >
                                        All Hospitals
                                    </div>
                                )}
                                {hospitals.map((hospital) => (
                                    <div
                                        key={hospital.id}
                                        onClick={() => setSelectedHospital(hospital)}
                                        style={{
                                            padding: "8px 16px",
                                            borderRadius: "20px",
                                            cursor: "pointer",
                                            border:
                                                selectedHospital?.id === hospital.id ? '1px solid #919191' : "none",
                                            color: "black",
                                            marginTop: '20px'
                                        }}
                                    >
                                        {hospital.name}
                                    </div>
                                ))}
                            </div>

                            <div
                                style={{
                                    display: "grid",
                                    width: "100%",
                                    height: "100%", 
                                }}
                            >
                                {/* EMPTY TOP LEFT */}
                                {/* TIME + blocks */}
                                {/* <Scrollbar gridTemplate="auto / 1fr 30px" padding="0 1rem" scrollerPadding="0rem" scrollerWidth="12px" style={{ height: "100%" }}  > */}
                                <TeamsCalendar weekDates={weekDates} weekSlots={weekSlots} selectedDate={selectedDate} />
                                {/* </Scrollbar> */}
                            </div>

                        </Paper>
                    </Box>
                </Grid>


                {/* RIGHT */}
                <Grid size={{ md: 4, sm: 12, xs: 12 }}>
                    <Box sx={{ px: 2 }}>

                        <Paper
                            elevation={5}
                            sx={{
                                ...paperStyle,
                                height: { xl: "79vh", lg: '80vh', md: '88vh' },
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden"
                            }}
                        >
                            <Typography sx={{ fontSize: '16px', textAlign: 'left', fontWeight: 'bold', margin: 2 }}>Delivery Schedules</Typography>

                            <Box sx={{ ...paperStyle, padding: '10px', margin: '12px' }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: '400', textAlign: 'left' }}>
                                    Drag & drop the card in the schedule
                                </Typography>
                                {/* , or click + to add a new delivery or event */}
                                <Box style={{ display: 'flex', flexDirection: 'row', marginTop: '4px' }}>
                                    <DraggableBox
                                        type="Delivery"
                                        duration={duration}
                                        setDuration={setDuration}
                                    />
                                    <DraggableBox
                                        type="Surgery"
                                        duration={surgery}
                                        setDuration={setSurgery}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        boxShadow: '0px 2px 4px rgba(0,0,0,0.15)',
                                        borderRadius: "10px",
                                        padding: "8px 10px",
                                        width: "fit-content",
                                        margin: "10px 5px"
                                    }}
                                >
                                    {/* EVENT TYPE */}
                                    <select
                                        value={manualEventType}
                                        onChange={(e) => setManualEventType(e.target.value)}
                                        style={{ border: "none", background: "transparent" }}
                                    >
                                        <option value="Delivery">Delivery</option>
                                        <option value="Surgery">Surgery</option>
                                    </select>

                                    {/* DATE */}
                                    <select
                                        value={manualDate || ""}
                                        onChange={(e) => {
                                            setManualDate(e.target.value);
                                            setManualSlot(null);
                                        }}
                                        style={{ border: "none", background: "transparent", maxWidth:'60px' }}
                                    >
                                        <option value="">Date</option>
                                        {weekDates.map((d) => (
                                            <option key={d.iso} value={d.iso}>
                                                {d.day} {d.label}
                                            </option>
                                        ))}
                                    </select>

                                    {/* SLOT */}
                                    <select
                                        value={manualSlot || ""}
                                        onChange={(e) => setManualSlot(e.target.value)}
                                        disabled={!manualDate}
                                        style={{ border: "none", background: "transparent" }}
                                    >
                                        <option value="">Select Slot</option>
                                        {selectedDaySlots.map((slot) => (
                                            <option key={slot.id} value={slot.id}>
                                                {formatUTC(new Date(slot.startDateTime))} -{" "}
                                                {formatUTC(new Date(slot.endDateTime))}
                                            </option>
                                        ))}
                                    </select>

                                    {/* ADD BUTTON */}
                                    <Button
                                        onClick={() => handleManualCreate()}
                                        sx={{
                                            minWidth: '20px',
                                            height: '20px',
                                            borderRadius: "10px",
                                            border: '1px solid black', color: 'black'
                                        }}
                                    >
                                        +
                                    </Button>
                                </Box>
                            </Box>
                            {groupedEvents.length > 0 && <Scrollbar gridTemplate="auto / 1fr 20px" padding="0 0rem" scrollerPadding="0rem" scrollerWidth="12px">
                                {groupedEvents.map((group, index) => {
                                    const type = group[0]?.eventType;
                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                ...paperStyle,
                                                padding: '12px',
                                                margin: '12px',
                                                display: 'flex',
                                                flexDirection: 'row',
                                            }}
                                        >
                                            <Box sx={{ width: '100px', }}>
                                                {/* HEADER */}
                                                <Typography sx={{ fontWeight: "bold", fontSize: 15, textAlign: 'left' }}>
                                                    {type}
                                                </Typography>

                                                {/* DATE */}
                                                <Typography sx={{ fontSize: 13, mb: 1, textAlign: 'left' }}>
                                                    {moment(group[0]?.startDateTime).format('D MMM YYYY')}
                                                </Typography>
                                            </Box>

                                            {/* TIMES WRAP */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: "6px", marginLeft: '12px',
                                                }}
                                            >
                                                {group.map((ev) => (
                                                    <Box
                                                        key={ev.id}
                                                        sx={{
                                                            background: "#FFF",
                                                            padding: "4px 6px",
                                                            borderRadius: "6px",
                                                            fontSize: "12px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            height: '20px'
                                                        }}
                                                    >
                                                        {formatUTC(new Date(ev.startDateTime))} -{" "}
                                                        {formatUTC(new Date(ev.endDateTime))}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Scrollbar>}
                        </Paper>
                    </Box>
                </Grid>
            </Grid>
            {/* MODAL */}
            {pendingMove && (
                <Box
                    style={{
                        position: "fixed",
                        top: "40%",
                        left: "40%",
                    }}
                >
                    <Paper
                        elevation={5}
                        sx={{
                            ...paperStyle
                            , padding: 2, borderColor: '#FFF',
                            top: "40%",
                            left: "40%",
                            minWidth: '158px',
                            backgroundColor:'#D3D3D3'
                        }}
                    >
                        <Typography>
                            {pendingMove?.type === "NEW"
                                ? `Add ${pendingMove.item.type} (${pendingMove.item.duration} min)?`
                                : pendingMove?.type == "BOOKING_MOVE" ?
                                    "Reschedule Bookings to"
                                    : `Move ${pendingMove.item.eventType}?`}
                        </Typography>
                        {(pendingMove?.type === "EVENT_MOVE" || pendingMove?.type === "BOOKING_MOVE") && <Typography>
                            {moment(findSlotById(pendingMove.toSlot)?.startDateTime).format("Do MMM")}, {formatUTC(new Date(findSlotById(pendingMove.toSlot)?.startDateTime))} - {formatUTC(new Date(findSlotById(pendingMove.toSlot)?.endDateTime))}
                        </Typography>}

                        <Box sx={{ justifyContent: 'space-evenly', marginTop: '20px', display: 'flex' }}>
                            <Button
                                variant="contained"
                                onClick={() => setPendingMove(null)}
                                sx={{ height: '19px', width: '58px', fontSize: '10px', backgroundColor: '#FFF', borderRadius: '13.8px', color: 'black' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => confirmMove()}
                                sx={{ height: '19px', width: '58px', fontSize: '10px', backgroundColor: '#FFF', borderRadius: '13.8px', color: 'black' }}
                            >
                                Confirm
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            )}

            {(blockSlot || unBlockSlot) && (
                <Box
                    style={{
                        position: "fixed",
                        top: "40%",
                        left: "40%",
                        backgroundColor: 'transparent',
                    }}
                >
                    <Paper
                        elevation={5}
                        sx={{
                            ...paperStyle
                            , padding: 2,
                            borderColor: '#FFF',
                            top: "40%",
                            left: "40%",
                            minWidth: '158px', 
                            backgroundColor:'#D3D3D3'
                        }}
                    >
                        <Typography>
                            {`Do you really want to ${blockSlot ? "Block" : "Unblock"} the slot?`}
                        </Typography>
                        {<Typography>
                            {moment(blockSlot ? blockSlot?.startDateTime : unBlockSlot?.startDateTime).format("Do MMM")}, {formatUTC(new Date(blockSlot ? blockSlot?.startDateTime : unBlockSlot?.startDateTime))} - {formatUTC(new Date(blockSlot ? blockSlot?.endDateTime : unBlockSlot?.endDateTime))}
                        </Typography>}

                        <Box sx={{ justifyContent: 'space-evenly', marginTop: '20px', display: 'flex' }}>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setBlockSlot(null)
                                    setUnBlockSlot(null)
                                }}
                                sx={{ height: '19px', width: '58px', fontSize: '10px', backgroundColor: '#FFF', borderRadius: '13.8px', color: 'black' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    confirmBlocking(blockSlot ? true : false)
                                }}
                                sx={{ height: '19px', width: '58px', fontSize: '10px', backgroundColor: '#FFF', borderRadius: '13.8px', color: 'black' }}
                            >
                                Confirm
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            )}

            {(deleteEvent) && (
                <Box
                    style={{
                        position: "fixed",
                        top: "40%",
                        left: "40%",
                        backgroundColor: 'transparent',
                    }}
                >
                    <Paper
                        elevation={5}
                        sx={{
                            ...paperStyle
                            , padding: 2,
                            borderColor: '#FFF',
                            top: "40%",
                            left: "40%",
                            minWidth: '158px',
                            backgroundColor:'#D3D3D3'
                        }}
                    >
                        <Typography>
                            {`Do you really want to delete the Event?`}
                        </Typography>

                        {<Typography>
                            {moment(deleteEvent?.startDateTime).format("Do MMM")}, {formatUTC(new Date(deleteEvent?.startDateTime))} - {formatUTC(new Date(deleteEvent?.endDateTime))}
                        </Typography>}

                        <Box sx={{ justifyContent: 'space-evenly', marginTop: '20px', display: 'flex' }}>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setDeleteEvent(null)
                                }}
                                sx={{ height: '19px', width: '58px', fontSize: '10px', backgroundColor: '#FFF', borderRadius: '13.8px', color: 'black' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    store.dispatch(scheduleAction.deleteScheduleEventRequestAction(deleteEvent.id))
                                    setDeleteEvent(null)
                                }}
                                sx={{ height: '19px', width: '58px', fontSize: '10px', backgroundColor: '#FFF', borderRadius: '13.8px', color: 'black' }}
                            >
                                Confirm
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            )}
            {/* The slot bulk-cancel API records the reason on refunds but does not notify patients */}
            <CancelReasonDialog
                open={Boolean(cancelBookings)}
                title="Cancel bookings"
                hint="Why are you cancelling? It is recorded with any refund."
                keepLabel="Keep bookings"
                confirmLabel="Cancel bookings"
                description={cancelBookings ? `Cancel all bookings on ${moment(cancelBookings.startDateTime).format("Do MMM")}, ${formatUTC(new Date(cancelBookings.startDateTime))} - ${formatUTC(new Date(cancelBookings.endDateTime))}?` : ''}
                onClose={() => setCancelBookings(null)}
                onConfirm={(reason) => {
                    store.dispatch(scheduleAction.cancelBulkAppointmentRequestAction(cancelBookings.id, cancellationReasonLabel(reason)))
                    setCancelBookings(null)
                }}
            />
        </DndContext>
    );
}

